<template>
  <div>
    <div class="list-toolbar">
      <el-select
        v-model="selectedProductId"
        placeholder="Select product"
        size="small"
        filterable
        class="product-select"
      >
        <el-option
          v-for="p in safeProducts"
          :key="p.id"
          :label="p.productNumber + ' (' + p.productName + ')'"
          :value="p.id"
        />
      </el-select>
      <el-button
        size="small"
        icon="el-icon-refresh"
        :loading="loading"
        @click="getHwVersions()"
        >Refresh</el-button
      >
    </div>

    <div v-if="hwVersions.length" class="version-section">
      <div class="version-label">Hardware</div>
      <div class="version-pills">
        <span
          v-for="item in hwVersions"
          :key="item"
          class="version-pill"
          :class="{ active: activeHw === item }"
          @click="activeHw = item"
          >{{ $t('text.hardware') }}-{{ item }}</span
        >
      </div>
    </div>
    <div v-if="fwVersions.length" class="version-section">
      <div class="version-label">Firmware</div>
      <div class="version-pills">
        <span
          v-for="item in fwVersions"
          :key="item"
          class="version-pill"
          :class="{ active: activeFw === item }"
          @click="activeFw = item"
          >V-{{ item }}</span
        >
      </div>
    </div>

    <div class="app-card">
      <el-table
        border
        :data="tableData"
        size="small"
        v-loading="loading"
        class="app-table"
        :max-height="tableHeight"
      >
        <el-table-column
          :label="$t('text.position')"
          prop="position"
          width="90"
          :fixed="screenWidth > 767"
        >
          <template slot-scope="{ row }">{{
            getPosition(row.position)
          }}</template>
        </el-table-column>
        <el-table-column
          label="Max Sensor"
          prop="constantA"
          width="100"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.constant') + ' B'"
          prop="constantB"
          width="100"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.constant') + ' C'"
          prop="constantC"
          width="100"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.constant') + ' D'"
          prop="constantD"
          width="100"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.yellow_threshold')"
          prop="yellowThreshold"
          width="120"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.red_threshold')"
          prop="redThreshold"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Const A"
          prop="urineConstantA"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Const B"
          prop="urineConstantB"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Const C"
          prop="urineConstantC"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Const D"
          prop="urineConstantD"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Yellow"
          prop="urineYellowThreshold"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Urine Red"
          prop="urineRedThreshold"
          width="100"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.firmware_version')"
          prop="fwVersion"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.hardware_version')"
          prop="hwVersion"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('text.max_capacity')"
          prop="maxCapacity"
          width="110"
          show-overflow-tooltip
        />
        <el-table-column
          label="Clumping Mult."
          prop="clumpingMultiplier"
          width="120"
          show-overflow-tooltip
        />
        <el-table-column
          label="Clumping"
          prop="isClumpingEnabled"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.isClumpingEnabled"
              @input="enableDisableClumping(row)"
            />
          </template>
        </el-table-column>
        <el-table-column
          v-if="p('Products.Product Characteristics.Edit')"
          :label="$t('text.action') | firstLetterCapitalize"
          width="300"
          :fixed="screenWidth > 767 ? 'right' : false"
        >
          <template slot-scope="{ row }">
            <div class="action-btns">
              <el-button size="mini" type="primary" plain @click="edit(row)"
                >View/Edit</el-button
              >
              <el-button
                size="mini"
                type="primary"
                plain
                @click="duplicate(row)"
                >Duplicate</el-button
              >
              <el-button size="mini" type="danger" plain @click="deleteRow(row)"
                >Delete</el-button
              >
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <EditProductCharacteristics ref="editProductCharacteristics" />
    <DuplicateCharacteristics ref="duplicateCharacteristics" />
  </div>
</template>

<script>
import ApiService from '@/services/CommonApiService.js'
import EditProductCharacteristics from '@/components/products/products/EditProductCharacteristics'
import DuplicateCharacteristics from '@/components/products/products/DuplicateCharacteristics'
import loraTableHeight from '@/mixins/loraTableHeight'
import swal from 'sweetalert'
export default {
  mixins: [loraTableHeight],
  data() {
    return {
      screenWidth: window.innerWidth,
      loading: false,
      tableData: [],
      hwVersions: [],
      fwVersions: [],
      activeHw: '',
      activeFw: '',
      positions: '',
      allProducts: []
    }
  },
  components: {
    EditProductCharacteristics,
    DuplicateCharacteristics
  },
  computed: {
    safeProducts() {
      const list = this.allProducts
      const sel = this.$store.state.product.selectedProduct
      if (sel && !list.find(p => p.id === sel.id)) return [sel, ...list]
      return list
    },
    selectedProductId: {
      get() {
        const p = this.$store.state.product.selectedProduct
        return p ? p.id : null
      },
      set(id) {
        const p = this.allProducts.find(x => x.id === id)
        if (p) this.$store.commit('product/SET_SELECTED_PRODUCT', p)
      }
    }
  },
  mounted() {
    this.getPositions()
    this.loadSelects()
    if (
      this.p('Products.Product Characteristics.Add') &&
      this.$parent.setPageAction
    ) {
      this.$parent.setPageAction({
        label: 'Add Characteristics',
        onClick: this.add
      })
    }
  },
  beforeDestroy() {
    if (this.$parent.clearPageAction) this.$parent.clearPageAction()
    window.removeEventListener('resize', this.onResize)
  },
  watch: {
    activeHw() {
      this.getFwVersions()
      this.getData()
    },
    activeFw() {
      this.getData()
    },
    '$store.state.product.selectedProduct'() {
      this.tableData = []
      this.hwVersions = []
      this.fwVersions = []
      this.activeHw = ''
      this.activeFw = ''
      this.getHwVersions()
    }
  },
  methods: {
    onResize() {
      this.screenWidth = window.innerWidth
    },
    async loadSelects() {
      const res = await ApiService.GetProducts()
      this.allProducts = res.data.data || []
      this.getHwVersions()
    },
    getPosition(val) {
      for (const key in this.positions) {
        if (this.positions[key] === val) return key
      }
    },
    getPositions() {
      ApiService.GetPosition()
        .then(response => {
          this.positions = response.data.data
        })
        .catch(() => {})
    },
    getHwVersions(updateActive = true) {
      if (!this.$store.state.product.selectedProduct) return
      this.loading = true
      ApiService.GetProductThresholdsByProductId(
        this.$store.state.product.selectedProduct.id
      )
        .then(response => {
          this.loading = false
          const hwVersions = response.data.data.map(i => i.hwVersion)
          this.hwVersions = Array.from(new Set(hwVersions))
          if (this.hwVersions.length && (updateActive || !this.activeHw)) {
            this.activeHw = this.hwVersions[0]
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    getFwVersions(updateActive = true) {
      if (!this.$store.state.product.selectedProduct) return
      this.loading = true
      ApiService.GetProductThresholdsByProductId(
        this.$store.state.product.selectedProduct.id
      )
        .then(response => {
          this.loading = false
          const fwVersions = response.data.data
            .filter(i => i.hwVersion === this.activeHw)
            .map(i => i.fwVersion)
          this.fwVersions = Array.from(new Set(fwVersions))
          if (this.fwVersions.length && (updateActive || !this.activeFw)) {
            this.activeFw = this.fwVersions[0]
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    getData() {
      if (!this.$store.state.product.selectedProduct) return
      this.loading = true
      ApiService.GetProductThresholdsByProductId(
        this.$store.state.product.selectedProduct.id
      )
        .then(response => {
          this.loading = false
          this.tableData = response.data.data.filter(
            i => i.hwVersion === this.activeHw && i.fwVersion === this.activeFw
          )
        })
        .catch(() => {
          this.loading = false
        })
    },
    add() {
      this.$refs.editProductCharacteristics.openDialog()
    },
    edit(row) {
      this.$refs.editProductCharacteristics.openDialog()
      this.$refs.editProductCharacteristics.form = JSON.parse(
        JSON.stringify(row)
      )
    },
    duplicate(row) {
      this.$refs.duplicateCharacteristics.openDialog()
      this.$refs.duplicateCharacteristics.form.ID = row.id
    },
    deleteRow(row) {
      this.$confirm(
        'Please keep in mind that deleting records is permanent.',
        this.$t('text.you_able_to_delete_data') + ' this record',
        {
          confirmButtonText: this.$t('text.delete').toUpperCase(),
          cancelButtonText: this.$t('text.cancel').toUpperCase(),
          confirmButtonClass: 'delete-btn',
          cancelButtonClass: 'cancel-btn',
          type: 'warning',
          customClass: 'delete-warning-box',
          showClose: false,
          closeOnClickModal: false
        }
      )
        .then(() => {
          this.handleDelete(row.id)
        })
        .catch(() => {})
    },
    handleDelete(id) {
      const isTheLatest = this.tableData.length <= 1
      const isLatestFw = this.fwVersions.length <= 1
      ApiService.DeleteProductThreshold(id)
        .then(response => {
          if (response.data.success) {
            this.getHwVersions(isTheLatest && isLatestFw)
            this.getFwVersions(isTheLatest)
            this.getData()
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: 'Deleted Failed',
              text: response.data.msg
            })
          }
        })
        .catch(() => {
          swal({ icon: 'error', timer: 2000, title: 'Deleted Failed' })
        })
    },
    enableDisableClumping(item) {
      ApiService.EnableDisableClumping(item.id, item.isClumpingEnabled)
        .then(response => {
          if (response.data.success) {
            this.getData()
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: 'Updated Failed',
              text: response.data.msg
            })
          }
        })
        .catch(() => {
          swal({ icon: 'error', timer: 2000, title: 'Updated Failed' })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.list-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.manufacturer-select {
  width: 200px;
}

.product-select {
  width: 260px;
}

.version-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.version-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text3, #9ca3af);
  white-space: nowrap;
  width: 60px;
  flex-shrink: 0;
}

.version-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.version-pill {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 99px;
  border: 1px solid var(--border);
  background: var(--bg);
  font-size: 12px;
  color: var(--text2, #6b7280);
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;

  &:hover {
    border-color: var(--blue, #2563eb);
    color: var(--blue, #2563eb);
  }

  &.active {
    background: var(--blue, #2563eb);
    border-color: var(--blue, #2563eb);
    color: #fff;
    font-weight: 600;
  }
}

.action-btns {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  align-items: center;
}
</style>
